'use client';

import { useEffect } from 'react';
import { getFormData } from '@nf/utils-common/form';
import { getAuthToken, useAuthToken, useAuthActions } from '@/store/auth';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { ApiQueryBasePromise } from '@/types';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { getResponseJwtHeader } from '@/utils/web';
import { useSiteStore } from '@/store/site';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { useGetSiteConfigs } from './use-site-configs';
import { useAccountInfo } from './account';
import { resultService } from '@/services/result.service';
import { LanguageHelper, useLanguageHelper } from '../use-language-helper';

export interface ResultDropdownItem {
	ID: number | string;
	Value?: string;
	Name?: string;
}
export interface ResultDropDownListNew {
	Item1: any;
	Item2?: any;
	Item3?: any;
	Item4?: any;
	ErrorCode?: number;
}
interface DropdownFilter {
	Selecteddate?: string,
	Selectedsport?: number,
	selectedgametype?: number
}

interface OutrightDropdownFilter {
	Selectedstartdate?: string,
	Selectedenddate?: string,
	Selectedsport?: number
}

interface ResultOptions {
	selectDate: string,
	selectSid: number,
	selectLid: number,
	selectSeaId: number,
	selectLgId: number
}

interface OutrightOptions {
	sDate: string,
	eDate: string,
	selectSid: number,
	selectLid: number,
}

export interface ResultIndex {
	Header: object;
	LeagueDic: object;
	SelectLeague: number;
	ErrorCode?: number;
}

export interface TabletSoccerDetail {
	[key: string]: any;
}

export interface CupMatchResult {
	AwayId: number;
	AwayName: string;
	AwayScore: number;
	HomeId: number;
	HomeName: string;
	HomeScore: number;
	GlobalShowTime: string;
	MatchStatus: number;
	Round: number;
	MatchId: number;
}

export interface TabletEsportDetail {
	[key: string]: any;
}

interface ResultUrl {
	Data: { Url: string };
	ErrorCode: number;
	ErrorDescription: string;
}
interface ResultUrlProps {
	groupId: number;
	date: string;
}

const getGmt = () => {
	return 0 - new Date().getTimezoneOffset() / 60;
}

const fetchResultDropDownListNew = async (formData: DropdownFilter): Promise<ApiQueryBasePromise<ResultDropDownListNew>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/GetResultDropDownListNew', apiDomain);
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData(formData)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const fetchResultDropDownListNewApiBackend = async (formData: DropdownFilter, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<ResultDropDownListNew>> => {
	const body = JSON.stringify({
		Selecteddate: formData.Selecteddate,
		Selectedsport: formData.Selectedsport?.toString(),
		selectedgametype: formData.selectedgametype?.toString(),
		GMT: getGmt()
	});
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/ResultDropDownList`), { body: body })(accessToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useResultDropDownListNew = (formData: DropdownFilter) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const authToken = useAuthToken();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_DROPDOWN_LIST_NEW, formData],
		queryFn: () => SiteConfigsData?.EnableResultNormalApiBackend ?
			fetchResultDropDownListNewApiBackend(formData, user?.ApiBackendUrl || '', user?.AccessToken || '') : fetchResultDropDownListNew(formData),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: Boolean(authToken)
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);
	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchOutrightResultDropDownListNew = async (formData: DropdownFilter): Promise<ApiQueryBasePromise<ResultDropDownListNew>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/GetOutrightResultDropDownListNew', apiDomain);
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData(formData)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const fetchOutrightResultDropDownListNewApiBackend = async (formData: OutrightDropdownFilter, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<ResultDropDownListNew>> => {
	const body = JSON.stringify({
		Selectedstartdate: formData.Selectedstartdate,
		Selectedenddate: formData.Selectedenddate,
		Selectedsport: formData.Selectedsport?.toString(),
		gmt: getGmt()
	});
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/OutrightDropDownList`), { body: body })(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useOutrightResultDropDownListNew = (formData: OutrightDropdownFilter) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_OUTRIGHT_DROPDOWN_LIST_NEW, formData],
		queryFn: () => SiteConfigsData?.EnableResultOutrightApiBackend ?
			fetchOutrightResultDropDownListNewApiBackend(formData, user?.ApiBackendUrl || '', user?.AccessToken || '') : fetchOutrightResultDropDownListNew(formData),
		cacheTime: 0,
		refetchOnWindowFocus: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchResultIndex = async (options: ResultOptions): Promise<ApiQueryBasePromise<ResultIndex>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/ResultIndex', apiDomain);
	const GMT = getGmt();
	apiUrl.searchParams.set('GMT', GMT.toString());

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData(options)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

const fetchResultIndexApiBackend = (languageHelper: LanguageHelper) => async (options: ResultOptions, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<ResultIndex>> => {
	const body = JSON.stringify({
		selectDate: options.selectDate,
		selectSid: options.selectSid.toString(),
		selectLid: options.selectLid.toString(),
		selectSeaId: options.selectSeaId.toString(),
		selectLgId: options.selectLgId,
		gmt: getGmt()
	});
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/ResultIndex`), { body: body })(accessToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	const data = await response.json();

	if (data) {
		resultService.getResultIndexHeaderFormat(languageHelper)(data, options.selectSid);
		resultService.getResultIndexMatchFormat(languageHelper)(data, options.selectSid);
	}

	return {
		data,
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useResultIndex = (options: ResultOptions, isChanged: boolean) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const languageHelper = useLanguageHelper();
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_INDEX, options],
		queryFn: () => SiteConfigsData?.EnableResultNormalApiBackend ?
			fetchResultIndexApiBackend(languageHelper)(options, user?.ApiBackendUrl || '', user?.AccessToken || '') :
			fetchResultIndex(options),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: true,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);
	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchOutrightIndex = async (options: OutrightOptions): Promise<ApiQueryBasePromise<ResultIndex>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/OutrightIndex', apiDomain);
	const GMT = 0 - new Date().getTimezoneOffset() / 60;
	apiUrl.searchParams.set('GMT', GMT.toString());

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData(options)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

const fetchOutrightIndexApiBackend = (languageHelper: LanguageHelper) => async (options: OutrightOptions, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<ResultIndex>> => {
	const body = JSON.stringify({
		sDate: options.sDate,
		eDate: options.eDate,
		selectSportType: options.selectSid.toString(),
		selectLeagueId: options.selectLid.toString(),
		gmt: 0 - new Date().getTimezoneOffset() / 60
	});
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/OutrightIndex`), { body: body })(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	let data = await response.json();

	if (data) {
		data = resultService.getOutrightIndexHeaderFormat(languageHelper)(data);
	}

	return {
		data: data,
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useOutrightIndex = (options: OutrightOptions, isChanged: boolean) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const languageHelper = useLanguageHelper();
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_OUTRIGHT_INDEX, options],
		queryFn: () => SiteConfigsData?.EnableResultOutrightApiBackend ?
			fetchOutrightIndexApiBackend(languageHelper)(options, user?.ApiBackendUrl || '', user?.AccessToken || '') : fetchOutrightIndex(options),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: isChanged,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchTabletSoccerDetail = async (matchId: number): Promise<ApiQueryBasePromise<TabletSoccerDetail>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/TabletSoccerDetail', apiDomain);

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData({ matchId: matchId })
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

const fetchTabletSoccerDetailApiBackend = (languageHelper: LanguageHelper) => async (matchId: number, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<TabletSoccerDetail>> => {
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/SoccerDetail`), { body: JSON.stringify({ matchId: matchId }) })(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	let data = await response.json();

	if (data && data.Data) {
		data = resultService.getResultDetailDataFormat(languageHelper)(data.Data, 1);
	}

	return {
		data,
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useTabletSoccerDetail = (matchId: number) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const languageHelper = useLanguageHelper();
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_TABLET_SOCCER_DETAIL, matchId],
		queryFn: () =>
			SiteConfigsData?.EnableResultDetailApiBackend ?
				fetchTabletSoccerDetailApiBackend(languageHelper)(matchId, user?.ApiBackendUrl || '', user?.AccessToken || '') :
				fetchTabletSoccerDetail(matchId),
		cacheTime: 0,
		refetchOnWindowFocus: false

	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);
	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchTabletEsportDetail = async (matchId: number): Promise<ApiQueryBasePromise<TabletEsportDetail>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('/Result/TabletEsportDetail', apiDomain);

	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(apiUrl.href), {
		body: getFormData({ matchId: matchId })
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

const fetchTabletEsportDetailApiBackend = (languageHelper: LanguageHelper) => async (matchId: number, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<TabletEsportDetail>> => {
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiBackendUrl}/Result/EsportDetail`), { body: JSON.stringify({ matchId: matchId }) })(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	let data = await response.json();

	if (data && data?.Data) {
		data = resultService.getResultDetailDataFormat(languageHelper)(data?.Data, 43);
	}

	return {
		data,
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useTabletEsportDetail = (matchId: number) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const languageHelper = useLanguageHelper();
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_TABLET_ESPORT_DETAIL, matchId],
		queryFn: () =>
			SiteConfigsData?.EnableResultDetailApiBackend ?
				fetchTabletEsportDetailApiBackend(languageHelper)(matchId, user?.ApiBackendUrl || '', user?.AccessToken || '') :
				fetchTabletEsportDetail(matchId),
		cacheTime: 0,
		refetchOnWindowFocus: false

	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

// Third Party
const fetchResultUrl = async (urlInfo: ResultUrlProps): Promise<ApiQueryBasePromise<ResultUrl>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('AuthorizationCustomer/GetResultUrl', apiDomain);
	const GMT = 0 - new Date().getTimezoneOffset() / 60;
	const [month, day, year] = urlInfo.date.split('/');
	apiUrl.searchParams.set('GroupId', urlInfo.groupId.toString());
	apiUrl.searchParams.set('homeUrl', window.location.hostname);
	apiUrl.searchParams.set('date', `${year}-${month}-${day}`);
	apiUrl.searchParams.set('GMT', GMT.toString());

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useResultUrl = (urlInfo: ResultUrlProps) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_URL, urlInfo],
		queryFn: () => fetchResultUrl(urlInfo),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchAuthorizationSportsLotteryResult = async (isDarkMode: boolean): Promise<ApiQueryBasePromise<string>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('AuthorizationCustomer/AuthorizationSportsLotteryResult', apiDomain);
	const GMT = 0 - new Date().getTimezoneOffset() / 60;
	apiUrl.searchParams.set('isDarkMode', isDarkMode.toString());
	apiUrl.searchParams.set('usertimezone', `${GMT}`);
	apiUrl.searchParams.set('isRedirect', '1');

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.text(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useAuthorizationSportsLotteryResult = (isDarkMode: boolean) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_AUTHORIZATION_SPORTS_LOTTERY_RESULT, isDarkMode],
		queryFn: () => fetchAuthorizationSportsLotteryResult(isDarkMode),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchAuthorizationIndo4DResult = async (isDarkMode: boolean): Promise<ApiQueryBasePromise<string>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('AuthorizationCustomer/AuthorizationIndo4DResult', apiDomain);
	apiUrl.searchParams.set('isDarkMode', isDarkMode.toString());

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.text(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useAuthorizationIndo4DResult = (isDarkMode: boolean) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_AUTHORIZATION_INDO_4D_RESULT, isDarkMode],
		queryFn: () => fetchAuthorizationIndo4DResult(isDarkMode),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchAuthorizationVirtualGameResult = async (isDarkMode: boolean): Promise<ApiQueryBasePromise<string>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('AuthorizationCustomer/AuthorizationVirtualGameResult', apiDomain);
	apiUrl.searchParams.set('isDarkMode', isDarkMode.toString());

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.text(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useAuthorizationVirtualGameResult = (isDarkMode: boolean) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_AUTHORIZATION_VIRTUAL_GAME_RESULT, isDarkMode],
		queryFn: () => fetchAuthorizationVirtualGameResult(isDarkMode),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchAuthorizationBF = async (date: string): Promise<ApiQueryBasePromise<string>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('AuthorizationCustomer/AuthorizationBF', apiDomain);
	const [month, day, year] = date.split('/');
	apiUrl.searchParams.set('cust', 'BF');
	apiUrl.searchParams.set('action', 'R');
	apiUrl.searchParams.set('date', `${year}${month}${day}`);

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.text(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useAuthorizationBF = (date: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_AUTHORIZATION_BULL_FIGHTING_RESULT, date],
		queryFn: () => fetchAuthorizationBF(date),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchN7LottoResult = async (): Promise<ApiQueryBasePromise<string>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const apiUrl = new URL('Result/GetN7LottoResult', apiDomain);

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl.href))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.text(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useN7LottoResult = () => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_N7_LOTTO_RESULT],
		queryFn: () => fetchN7LottoResult(),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchCupMatchResultApiBackend = async (tournamentId: string, apiBackendUrl: string, accessToken: string): Promise<ApiQueryBasePromise<CupMatchResult[]>> => {
	const response = await fetchComposer.postWithBearer(
		cloudUrlParser(`${apiBackendUrl}/Result/CupMatchResult?tournamentId=${tournamentId}`),
		{ body: JSON.stringify({ tournamentId }) },
	)(accessToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useCupMatchResult = (tournamentId: string) => {
	const { user } = useAccountInfo();
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.RESULT_CUP_MATCH_RESULT, tournamentId],
		queryFn: () => fetchCupMatchResultApiBackend(tournamentId, user?.ApiBackendUrl || '', user?.AccessToken || ''),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: Boolean(tournamentId)
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};